import { useState, useMemo, useCallback } from 'react';

import type { ConsignmentDisposition } from '@/entities/consignments';
import type { DataTableRow } from '@/shared/ui/data-table';

export interface FlattenedRow extends DataTableRow {
  level: number;
  parentId?: string;
  description: string;
  totalPerSrf?: number;
  unit?: string;
  remainsWithSender?: number;
  cancelledBySender?: number;
  receivedFromSender?: number;
  received?: number;
  transferOut?: number;
  transferIn?: number;
  stockOnHand?: number;
  reportedLost?: number;
  releasedToReceiver?: number;
  released?: number;
  date?: string;
  quantity?: number;
  o?: number;
  type?: 'line' | 'location' | 'movement';
  children?: FlattenedRow[];
}

export function useDispositionTable(dispositions: ConsignmentDisposition[]) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const flattenData = useCallback((data: ConsignmentDisposition[]): FlattenedRow[] => {
    const result: FlattenedRow[] = [];

    data.forEach((line, lineIdx) => {
      const lineRow: FlattenedRow = {
        id: `line-${lineIdx}`,
        level: 0,
        type: 'line',
        description: line.description,
        totalPerSrf: line.totalPerSrf,
        unit: line.unit,
        remainsWithSender: line.remainsWithSender,
        cancelledBySender: line.cancelledBySender,
        receivedFromSender: line.receivedFromSender,
        transferOut: line.transferOut,
        transferIn: line.transferIn,
        stockOnHand: line.stockOnHand,
        reportedLost: line.reportedLost,
        releasedToReceiver: line.releasedToReceiver,
        o: line.o ?? lineIdx + 1,
        children: []
      };

      result.push(lineRow);

      line.locations.forEach((loc, locIdx) => {
        const locRow: FlattenedRow = {
          id: `loc-${lineIdx}-${locIdx}`,
          level: 1,
          parentId: lineRow.id,
          type: 'location',
          description: loc.locationName,
          received: loc.received,
          transferOut: loc.transferOut,
          transferIn: loc.transferIn,
          stockOnHand: loc.stockOnHand,
          reportedLost: loc.reportedLost,
          released: loc.released,
          children: []
        };

        lineRow.children!.push(locRow);
        result.push(locRow);

        loc.movements.forEach((mov, movIdx) => {
          const movRow: FlattenedRow = {
            id: `mov-${lineIdx}-${locIdx}-${movIdx}`,
            level: 2,
            parentId: locRow.id,
            type: 'movement',
            description: mov.description || '',
            date: mov.date,
            quantity: mov.quantity !== 0 ? mov.quantity : undefined,
            stockOnHand: mov.stockOnHand
          };
          locRow.children!.push(movRow);
          result.push(movRow);
        });
      });
    });

    return result;
  }, []);

  const flatData = useMemo(() => flattenData(dispositions), [dispositions, flattenData]);

  const handleRowExpand = useCallback((rowId: string, expanded: boolean) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (expanded) {
        next.add(rowId);
      } else {
        next.delete(rowId);
      }
      return next;
    });
  }, []);

  const getRowChildren = useCallback((row: FlattenedRow) => row.children || [], []);
  const getRowLevel = useCallback((row: FlattenedRow) => row.level, []);

  const getRowClassName = useCallback((row: FlattenedRow) => {
    switch (row.level) {
      case 0:
        return 'bg-muted/5 font-semibold group';
      case 1:
        return 'bg-blue-50/10 [&>td:first-child]:border-l-4 [&>td:first-child]:border-l-blue-400 group/loc';
      case 2:
        return 'bg-transparent group/mov [&>td:first-child]:border-l-4 [&>td:first-child]:border-l-blue-200';
      default:
        return '';
    }
  }, []);

  return {
    flatData,
    expandedRows,
    handleRowExpand,
    getRowChildren,
    getRowLevel,
    getRowClassName
  };
}
